/**
 * effectLayerOps — pure list operations for authoring a level's `effectLayers`
 * (the EffectLayerPanel editor surface, Scene FX v3 M3 P4.4). Kept pure + side-
 * effect-free so the editor wiring (EditorSystem.setEffectLayers) and the panel
 * both reuse the same tested logic; the panel does the persistence + live preview.
 *
 * `DEFAULT_EFFECT_LAYER(kind)` is scraped from the real ATMOSPHERE_PRESETS — every
 * kind is used in at least one biome preset, so adding a kind seeds a realistic,
 * already-tuned layer instead of a hand-authored stub (and never drifts from the
 * specs). A fresh stable `id` is stamped so React keys + per-layer actions work.
 */
import type { EffectLayer, EffectKind } from '../../../config/atmosphere/effectLayer.types';
import { ATMOSPHERE_PRESETS } from '../../../config/atmosphere/presets.config';

let __fxLayerSeq = 0;
/** A stable, unique id for an editor effect-layer (React keys + per-layer ops). */
export function newEffectLayerId(kind: string): string {
  __fxLayerSeq += 1;
  return `fx-${kind}-${__fxLayerSeq}`;
}

/** Every kind that appears in any shipped preset — the kind-picker's options. */
export const AVAILABLE_EFFECT_KINDS: EffectKind[] = (() => {
  const seen = new Set<EffectKind>();
  for (const preset of Object.values(ATMOSPHERE_PRESETS)) {
    for (const layer of preset.layers) seen.add(layer.kind);
  }
  return [...seen];
})();

// First occurrence of each kind across all presets — the realistic default.
const EXAMPLE_BY_KIND = (() => {
  const byKind = new Map<EffectKind, EffectLayer>();
  for (const preset of Object.values(ATMOSPHERE_PRESETS)) {
    for (const layer of preset.layers) {
      if (!byKind.has(layer.kind)) byKind.set(layer.kind, layer);
    }
  }
  return byKind;
})();

/** A fresh, realistic default layer for `kind` (scraped from a real preset) with a new id. */
export function DEFAULT_EFFECT_LAYER(kind: EffectKind): EffectLayer {
  const example = EXAMPLE_BY_KIND.get(kind);
  // Deep-clone so editing the new layer never mutates the shared preset object.
  const base = example ? JSON.parse(JSON.stringify(example)) : { kind };
  return { ...base, kind, id: newEffectLayerId(kind) } as EffectLayer;
}

/** Stamp an id onto any layer missing one (preset layers + legacy levels). */
export function ensureEffectLayerIds(layers: EffectLayer[]): EffectLayer[] {
  return layers.map((l) => (l.id ? l : { ...l, id: newEffectLayerId(l.kind) }));
}

/** Append a fresh default layer of `kind`. */
export function addEffectLayer(layers: EffectLayer[], kind: EffectKind): EffectLayer[] {
  return [...layers, DEFAULT_EFFECT_LAYER(kind)];
}

/** Drop the layer with `id` (no-op if absent). */
export function removeEffectLayer(layers: EffectLayer[], id: string): EffectLayer[] {
  return layers.filter((l) => l.id !== id);
}

/** Swap the layer with `id` one slot toward the front (dir -1) or back (dir +1). */
export function moveEffectLayer(layers: EffectLayer[], id: string, dir: -1 | 1): EffectLayer[] {
  const i = layers.findIndex((l) => l.id === id);
  if (i < 0) return layers;
  const j = i + dir;
  if (j < 0 || j >= layers.length) return layers;
  const out = [...layers];
  [out[i], out[j]] = [out[j], out[i]];
  return out;
}

/** Shallow-merge `patch` into the layer with `id` (id immutable). */
export function updateEffectLayer(layers: EffectLayer[], id: string, patch: Partial<EffectLayer>): EffectLayer[] {
  return layers.map((l) => (l.id === id ? ({ ...l, ...patch, id, kind: l.kind } as EffectLayer) : l));
}
